import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Award, FileCheck, Building2, Search as SearchIcon, ShoppingBag, Shield, Clock, Users, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { PricingSection } from '../components/PricingSection';
import { TestimonialSlider } from '../components/TestimonialSlider';
import { ProcessSlider } from '../components/ProcessSlider';
import { CTABanner } from '../components/CTABanner';
import { FAQSection } from '../components/FAQSection';
import { services, countryCodes } from '../mock';
import { toast } from 'sonner';
import { useScrollAnimation, useCounterAnimation } from '../hooks/useScrollAnimation';
import { getGeoLocation, submitLead } from '../services/api';

const iconMap = {
  'award': Award,
  'file-badge': FileCheck,
  'building-2': Building2,
  'search-check': SearchIcon,
  'shopping-bag': ShoppingBag
};

// Services Section Component with Scroll Animation
const ServicesSection = ({ onOpenPopup }) => {
  const [titleRef, titleVisible] = useScrollAnimation(0.2);
  const [servicesRef, servicesVisible] = useScrollAnimation(0.2);

  const allItems = [
    ...services.map(service => ({...service, type: 'service'})),
    { id: 'cta', type: 'cta' }
  ];

  const ServiceCard = ({ item }) => {
    if (item.type === 'cta') {
      return (
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border border-blue-600 flex flex-col h-full">
          <div className="bg-white/20 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-white mb-3">
            Need Help Choosing?
          </h3>
          <p className="text-base text-white/95 leading-relaxed mb-4 flex-1">
            Get expert guidance on which service is right for your business. Our team is ready to help.
          </p>
          <Button
            onClick={onOpenPopup}
            className="w-full bg-white text-blue-600 hover:bg-gray-100 h-11 text-base font-semibold shadow-lg"
          >
            Get Free Consultation
          </Button>
        </div>
      );
    }

    const Icon = iconMap[item.icon];
    return (
      <Link
        to={item.link}
        className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all group border border-gray-200 h-full flex flex-col"
      >
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:from-primary/20 group-hover:to-primary/10 transition-all">
          <Icon className="text-primary" size={28} />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
          {item.title}
        </h3>
        <p className="text-base text-gray-600 leading-relaxed mb-3 flex-1">{item.description}</p>
        <div className="flex items-center text-primary font-semibold text-sm group-hover:gap-2 transition-all">
          Learn More <ArrowRight size={16} className="ml-1" />
        </div>
      </Link>
    );
  };

  return (
    <>
      <div className="container mx-auto px-4">
        <div 
          ref={titleRef}
          className={`text-center mb-16 scroll-animate scroll-fade-in ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Services</h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive intellectual property solutions tailored to your business needs
          </p>
        </div>
        
        {/* Mobile: Carousel/Slider */}
        <div className="md:hidden" ref={servicesRef}>
          <Carousel className="w-full max-w-sm mx-auto">
            <CarouselContent>
              {allItems.map((item) => (
                <CarouselItem key={item.id}>
                  <div className="p-1">
                    <ServiceCard item={item} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>

        {/* Desktop: Grid */}
        <div 
          ref={servicesRef}
          className={`hidden md:grid grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto stagger-children ${servicesVisible ? 'visible' : ''}`}
        >
          {allItems.map((item) => (
            <ServiceCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </>
  );
};

// Why Choose Us Component with Scroll Animation
const WhyChooseUs = () => {
  const [titleRef, titleVisible] = useScrollAnimation(0.2);
  const [cardsRef, cardsVisible] = useScrollAnimation(0.2);
  const [imagesRef, imagesVisible] = useScrollAnimation(0.2);

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div 
          ref={titleRef}
          className={`text-center mb-12 scroll-animate scroll-fade-in ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We combine expertise, efficiency, and personalized service to deliver exceptional trademark protection
          </p>
        </div>
        
        {/* Mobile: Carousel */}
        <div className="md:hidden mb-12" ref={cardsRef}>
          <Carousel className="w-full max-w-sm mx-auto">
            <CarouselContent>
              <CarouselItem>
                <div className="p-1">
                  <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield className="text-blue-600" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Protection</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Years of experience in trademark law ensuring your intellectual property is secured with precision and care
                    </p>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="p-1">
                  <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
                    <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="text-orange-600" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Processing</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Streamlined application process that gets your trademark filed quickly without compromising quality
                    </p>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem>
                <div className="p-1">
                  <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="text-blue-600" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Personalized guidance throughout your trademark journey with dedicated case managers available when you need them
                    </p>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>

        {/* Desktop: Grid */}
        <div 
          ref={cardsRef}
          className={`hidden md:grid md:grid-cols-3 gap-8 mb-12 stagger-children ${cardsVisible ? 'visible' : ''}`}
        >
          <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="text-blue-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Protection</h3>
            <p className="text-base text-gray-600 leading-relaxed">
              Years of experience in trademark law ensuring your intellectual property is secured with precision and care
            </p>
          </div>
          
          <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
            <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="text-orange-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Processing</h3>
            <p className="text-base text-gray-600 leading-relaxed">
              Streamlined application process that gets your trademark filed quickly without compromising quality
            </p>
          </div>
          
          <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="text-blue-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
            <p className="text-base text-gray-600 leading-relaxed">
              Personalized guidance throughout your trademark journey with dedicated case managers available when you need them
            </p>
          </div>
        </div>

        {/* Image Section */}
        <div 
          ref={imagesRef}
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 mt-16 scroll-animate scroll-scale ${imagesVisible ? 'visible' : ''}`}
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzYyNTQ5MDI5fDA&ixlib=rb-4.1.0&q=85" 
              alt="Business success" 
              className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">Proven Success</h3>
                <p className="text-white/90">Join thousands of satisfied clients who trust us</p>
              </div>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img 
              src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a" 
              alt="Professional team" 
              className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
              <div className="text-white">
                <h3 className="text-2xl font-bold mb-2">Expert Team</h3>
                <p className="text-white/90">Experienced professionals dedicated to your success</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Animated Trust Bar Component
const TrustBar = () => {
  const [ref1, count1] = useCounterAnimation(10000, 2000);
  const [ref2, count2] = useCounterAnimation(98, 2000);
  const [ref3, count3] = useCounterAnimation(15, 2000);

  return (
    <section className="py-8 bg-gray-50 border-y border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap items-center justify-center gap-12 text-center">
          <div ref={ref1}>
            <div className="text-3xl font-bold text-primary mb-1 counter-animate">
              {count1.toLocaleString()}+
            </div>
            <p className="text-sm text-gray-600">Trademarks Filed</p>
          </div>
          <div className="h-12 w-px bg-gray-300 hidden md:block"></div>
          <div ref={ref2}>
            <div className="text-3xl font-bold text-primary mb-1 counter-animate">
              {count2}%
            </div>
            <p className="text-sm text-gray-600">Success Rate</p>
          </div>
          <div className="h-12 w-px bg-gray-300 hidden md:block"></div>
          <div ref={ref3}>
            <div className="text-3xl font-bold text-primary mb-1 counter-animate">
              {count3}+ Years
            </div>
            <p className="text-sm text-gray-600">Experience</p>
          </div>
          <div className="h-12 w-px bg-gray-300 hidden md:block"></div>
          <div>
            <div className="text-3xl font-bold text-primary mb-1">24/7</div>
            <p className="text-sm text-gray-600">Support</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Hero Form Component (compact version)
const HeroForm = () => {
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    contactNumber: '',
    message: ''
  });

  useEffect(() => {
    fetchGeoLocation();
  }, []);

  const fetchGeoLocation = async () => {
    const result = await getGeoLocation();
    if (result.success) {
      setGeoData(result.data);
      const countryCode = countryCodes.find(c => c.country === result.data.countryCode);
      if (countryCode) {
        setFormData(prev => ({ 
          ...prev, 
          countryCode: countryCode.code,
          country: result.data.countryCode 
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      
      // Clean phone number - remove all non-numeric characters
      const cleanedPhone = formData.contactNumber.replace(/\D/g, '');
      
      // Validate phone number length (should be at least 7 digits)
      if (cleanedPhone.length < 7) {
        toast.error('Please enter a valid phone number (at least 7 digits)');
        setIsLoading(false);
        return;
      }

      const leadData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        countryDialCode: dialCode,
        country: formData.country,
        phoneNumber: cleanedPhone,
        description: formData.message.trim() || 'Hero form submission from homepage'
      };

      const result = await submitLead(leadData, geoData);

      if (result.success) {
        toast.success('Thank you! Your application has been submitted successfully.');
        
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          country: 'US',
          contactNumber: '',
          message: ''
        });
        
        // Redirect to thank you page with customer ID
        setTimeout(() => {
          navigate(result.redirectUrl);
        }, 1000);
      } else {
        toast.error(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Name *</label>
        <Input
          required
          disabled={isLoading}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Smith"
          className="h-9 text-sm text-gray-900"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
        <Input
          type="email"
          required
          disabled={isLoading}
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@company.com"
          className="h-9 text-sm text-gray-900"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Number *</label>
        <div className="flex gap-2">
          <Select
            value={formData.countryCode}
            onValueChange={(value) => {
              const selected = countryCodes.find(c => c.code === value);
              setFormData({ 
                ...formData, 
                countryCode: value,
                country: selected?.country || 'US'
              });
            }}
            disabled={isLoading}
          >
            <SelectTrigger className="w-24 h-9 text-sm text-gray-900">
              <SelectValue placeholder="+1" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {countryCodes.map((item) => (
                <SelectItem key={item.country} value={item.code}>
                  {item.code} {item.country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="tel"
            required
            disabled={isLoading}
            value={formData.contactNumber}
            onChange={(e) => {
              // Remove all non-numeric characters and limit to 15 digits
              const value = e.target.value.replace(/\D/g, '').slice(0, 15);
              setFormData({ ...formData, contactNumber: value });
            }}
            onKeyPress={(e) => {
              // Prevent non-numeric key presses
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            placeholder="5551234567"
            maxLength={15}
            pattern="[0-9]*"
            inputMode="numeric"
            className="flex-1 h-9 text-sm text-gray-900"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Message</label>
        <Textarea
          disabled={isLoading}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder="I need help registering my trademark for..."
          rows={2}
          className="resize-none text-sm text-gray-900"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full bg-primary hover:bg-primary/90 h-10 text-sm font-semibold shadow-lg hover:shadow-xl transition-all"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Get Started Today →'
        )}
      </Button>
    </form>
  );
};

export const Home = ({ onOpenPopup }) => {
  return (
    <div className="pt-20">
      <Helmet>
        <title>Trademark Registration Services | Protect Your Brand with Expert Legal Guidance</title>
        <meta name="description" content="Professional trademark registration services with comprehensive protection. Expert legal guidance, trademark searches, and brand protection for businesses nationwide. Start protecting your brand today." />
        <meta name="keywords" content="trademark registration, brand protection, trademark search, intellectual property, USPTO filing, trademark attorney, brand registration" />
        <meta property="og:title" content="Trademark Registration Services | Protect Your Brand" />
        <meta property="og:description" content="Professional trademark registration services with comprehensive protection. Expert legal guidance for businesses nationwide." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Trademark Registration Services | Protect Your Brand" />
        <meta name="twitter:description" content="Professional trademark registration services with comprehensive protection. Expert legal guidance for businesses nationwide." />
      </Helmet>
      {/* Creative Hero Section with Form - Optimized for 1080px */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 text-white py-10 md:py-12 overflow-hidden min-h-[calc(100vh-80px)] max-h-[1000px] flex items-center">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {/* Decorative Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1480944657103-7fed22359e1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJ1c2luZXNzfGVufDB8fHx8MTc2MjU0ODk4OXww&ixlib=rb-4.1.0&q=85" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Floating Decorative Icons */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block">
          <div className="absolute top-20 left-1/4 animate-float" style={{ animationDelay: '0s' }}>
            <Shield className="w-12 h-12 text-white/20" />
          </div>
          <div className="absolute top-32 right-1/4 animate-float" style={{ animationDelay: '2s' }}>
            <Award className="w-14 h-14 text-white/20" />
          </div>
          <div className="absolute bottom-24 left-1/3 animate-float" style={{ animationDelay: '1s' }}>
            <CheckCircle className="w-12 h-12 text-white/20" />
          </div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-block bg-white/20 backdrop-blur-md rounded-full px-5 py-2 mb-5 animate-slide-in">
                <span className="text-sm md:text-base font-bold">🏆 Trusted by 10,000+ Businesses Nationwide</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 leading-tight animate-slide-in">
                Protect Your Brand<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-100 to-white">Legacy</span>
              </h1>
              <p className="text-base md:text-lg mb-6 text-white/95 leading-relaxed animate-slide-in max-w-2xl">
                Comprehensive trademark protection services that safeguard your business identity and intellectual property rights with expert legal guidance
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start animate-slide-in">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm h-11 px-7 text-base font-semibold"
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Services
                </Button>
              </div>
            </div>

            {/* Right Form - Compact */}
            <div className="bg-white rounded-2xl p-5 shadow-2xl backdrop-blur-lg animate-slide-in">
              <div className="text-center mb-4">
                <div className="bg-primary/10 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <Shield className="text-primary" size={24} />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Start Your Trademark Registration</h2>
                <p className="text-xs text-gray-600">Fill out the form and we'll contact you within 24 hours</p>
              </div>
              
              <HeroForm />
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80L60 73.3C120 66.7 240 53.3 360 46.7C480 40 600 40 720 43.3C840 46.7 960 53.3 1080 56.7C1200 60 1320 60 1380 60L1440 60V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0V80Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Informative Section with Image */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch max-w-7xl mx-auto">
            {/* Left - Image */}
            <div className="order-2 lg:order-1 flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img 
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHBsYW5uaW5nfGVufDB8fHx8MTc2MjU0OTAyOXww&ixlib=rb-4.1.0&q=85" 
                  alt="Trademark protection strategy" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent"></div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="order-1 lg:order-2 flex flex-col justify-between">
              <div>
                <div className="inline-block bg-blue-100 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  Why Trademark Protection Matters
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Secure Your Business Identity Before It's Too Late
                </h2>
                <p className="text-base text-gray-600 leading-relaxed mb-6">
                  Your brand is your most valuable asset. Without proper trademark protection, you risk losing your business identity to competitors or copycats. Every day without protection is a day your brand remains vulnerable.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 rounded-full p-2 mt-1">
                      <CheckCircle className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Exclusive Rights</h3>
                      <p className="text-sm text-gray-600">Gain nationwide protection and exclusive rights to use your trademark in your industry</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 rounded-full p-2 mt-1">
                      <CheckCircle className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Legal Protection</h3>
                      <p className="text-sm text-gray-600">Prevent others from using confusingly similar names or logos that could harm your business</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 rounded-full p-2 mt-1">
                      <CheckCircle className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Brand Value</h3>
                      <p className="text-sm text-gray-600">Increase your business valuation with registered intellectual property assets</p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={onOpenPopup}
                className="bg-primary hover:bg-primary/90 h-11 px-8 text-base font-semibold w-fit"
              >
                Protect Your Brand Now
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar with Animated Counters */}
      <TrustBar />

      {/* Why Choose Us with Images */}
      <WhyChooseUs />

      {/* Services Section with Enhanced Design */}
      <section id="services" className="py-24 md:py-32 bg-gradient-to-br from-gray-50 to-gray-100">
        <ServicesSection onOpenPopup={onOpenPopup} />
      </section>

      {/* CTA Banner */}
      <CTABanner onOpenPopup={onOpenPopup} />

      {/* Process Section */}
      <ProcessSlider />

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* FAQ Section */}
      <FAQSection />

      {/* Testimonials */}
      <TestimonialSlider />

      {/* CTA Section with Background Image */}
      <section className="relative py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJ1c2luZXNzfGVufDB8fHx8MTc2MjU0ODk4OXww&ixlib=rb-4.1.0&q=85" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4">Ready to Protect Your Brand?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            Start your trademark registration today and secure your business identity for years to come
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white text-blue-600 hover:bg-gray-100 hover:scale-105 transition-all shadow-xl h-12 px-8 text-base"
          >
            Get Started Now
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </section>
    </div>
  );
};